import React from 'react'
import styled from 'styled-components'
import SEO from 'src/components/common/SEO'

import Layout from 'src/components/common/Layout'

import { LogoSyncContextProvider } from 'src/components/common/AnnotationContext'
import { useSelector } from 'react-redux'
import { selectAnalyticsParameters } from 'src/modules/reducers/analytics'
import { createUrl } from 'src/modules/utils/createUrl'
import { Link } from 'gatsby'
import LAPlanCards from 'src/components/common/LAPlanCards'
import MatchTicker from 'src/components/common/MatchTicker'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import { ratePlanCodes } from 'src/constants/ratePlanCode'

import Image from 'src/components/common/Image'
import {
  ChannelSection,
  DealSection,
  FAQSection,
  HeroSection,
  NetworkSection,
  PlatformSection,
  ExtraCTABanner,
} from 'src/components/common'
import { PlanCards } from 'src/components/common/PlanCards'
import { body, spacing, typographyMixin } from 'src/constants/marketing-ds'
import HeaderSignIn from 'src/components/common/HeaderSignIn'
import { WEB_MARKETING_HEADER_SIGN_IN } from 'src/modules/reducers/cohorts/constants'
import { useCohortFlag } from 'src/modules/hooks/useCohortFlag'

const CashAppFootnoteStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  white-space: nowrap;
  margin-top: 30px;
  margin-bottom: ${spacing.md};
  color: white;
  ${typographyMixin(body.sm)}
  img {
    width: 114px;
    margin-top: 0;
    margin-left: ${spacing.xxxs};
    object-fit: contain;
  }
`

const CashAppLogo = () => (
  <Image src="https://assets-imgx.fubo.tv/marketing/lp/images/apps/cash-app-logo-text-w.png?w=200" />
)

const LatinoTemplateDefault = ({
  lang = 'en',
  market = 'latino',
  ctaText,
  // Offer Code
  offerCode = ratePlanCodes.latino,
  offerBanner,
  // Metadata
  pageTitle,
  pageDescription,
  // Hero Section
  title,
  subtitle,
  tagline,
  video,
  staticAnnotation,
  annotations,
  poster = 'https://assets-imgx.fubo.tv/marketing/lp/images/posters/AdobeStock_210894329.jpg?w=2048&ch=width&auto=format,compress',
  posterMobile,
  noteLink,
  // Plan Cards
  disableLAPlanCards,
  discountedPrice,
  cardFootnote,
  // Network Section
  networkHeadline,
  networkSet = 'entertainmentLatino',
  customNetworks,
  // Others
  team,
  // AB Test for MatchTicker
  matchtickerSportId,
  matchtickerLeagueName,
  matchtickerLeagueId,
  // Force Anchor Link
  forceAnchorLink,
  // Personalization Params
  sportId,
  leagueId,
  teamId,
  heroFootnote,
  disableFreeTrial,
  additionalFAQs,
  // Fanduel RSN Data
  hasPlanCards,
  plans,
  plansSharedData,
  laPromoImg,
  noIndex,
  logo,
  logoHeight,
}) => {
  //REMOVED: univision_removed
  // let metaDescription =
  //   pageDescription ||
  //   (lang === 'es'
  //     ? 'Transmite canales de TUDN, Univision, beIN SPORTS y más con Fubo. No te pierdas ni un momento de los equipos y programas que más te gustan: visualízalos en directo y a la carta desde tu ordenador, smartphone, tableta, Roku, Fire TV, Apple TV y Chromecast.'
  //     : "Stream TUDN, Univision, beIN SPORTS channels and more with Fubo. Don't miss a moment of the teams and shows you love - watch live and on-demand from your desktop, smartphone, tablet, Roku, Fire TV, Apple TV and Chromecast.")

  //REPLACED: univision_removed
  let metaDescription =
    pageDescription ||
    (lang === 'es'
      ? 'Transmite canales de beIN SPORTS y más con Fubo. No te pierdas ni un momento de los equipos y programas que más te gustan: visualízalos en directo y a la carta desde tu ordenador, smartphone, tableta, Roku, Fire TV, Apple TV y Chromecast.'
      : "Stream beIN SPORTS channels and more with Fubo. Don't miss a moment of the teams and shows you love - watch live and on-demand from your desktop, smartphone, tablet, Roku, Fire TV, Apple TV and Chromecast.")

  const stateForProtobuf = useSelector(selectStateForProtobuf)
  const isAnchor = forceAnchorLink && (!disableLAPlanCards || hasPlanCards) ? '#select-plan' : null
  const originalParams = useSelector(selectAnalyticsParameters)
  const showHeaderSignIn = useCohortFlag(WEB_MARKETING_HEADER_SIGN_IN)
  let noteUrl
  if (noteLink) {
    noteUrl = createUrl(noteLink, originalParams)
  }

  const handleLocaleSwitch = () => {
    fireUIInteractionEvent(
      {
        element: noteUrl,
        component: 'lp_locale_switch_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  let noteText =
    lang === 'es' ? (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        To learn more in English, <span>click here</span>.
      </Link>
    ) : (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        Para traducir a español, <span>haga clic aquí</span>.
      </Link>
    )

  heroFootnote = heroFootnote ?? (
    <CashAppFootnoteStyled>
      {lang === 'es' ? 'Ahora aceptamos' : 'Now accepting'}
      <CashAppLogo />
    </CashAppFootnoteStyled>
  )

  return (
    <Layout
      lang={lang}
      market={market}
      offerCode={offerCode}
      offerBanner={offerBanner}
      forceLang={lang === 'es' ? 'es' : false}
      sportId={sportId}
      leagueId={leagueId}
      teamId={teamId}
      noIndex={noIndex}
    >
      {showHeaderSignIn && (
        <HeaderSignIn
          lang={lang}
          market={market}
          disableFreeTrial={disableFreeTrial}
          ctaText={ctaText}
        />
      )}
      <LogoSyncContextProvider annotations={annotations}>
        <PageWrapper>
          <SEO
            lang={lang}
            market={market}
            title={pageTitle || title}
            description={metaDescription}
          />
          {matchtickerLeagueId && (
            <MatchTicker
              lang={lang}
              market={market}
              sportId={matchtickerSportId}
              leagueName={matchtickerLeagueName}
              leagueId={matchtickerLeagueId}
            />
          )}
          <HeroSection
            lang={lang}
            logo={logo}
            logoHeight={logoHeight}
            market={market}
            heroBgVideo={video}
            title={title}
            subtitle={subtitle}
            tagline={tagline}
            staticAnnotation={staticAnnotation}
            posterImg={poster}
            ctaText={ctaText}
            noteText={noteUrl && noteText}
            footnote={heroFootnote}
            ctaHrefOverride={isAnchor}
            posterImgMobile={posterMobile}
            disableFreeTrial={disableFreeTrial}
          />
          {!disableLAPlanCards && (
            <LAPlanCards
              lang={lang}
              ctaText={ctaText}
              offerCode={offerCode}
              discountedPrice={discountedPrice}
              cardFootnote={cardFootnote}
              disableFreeTrial={disableFreeTrial}
              market={market}
              promoImg={laPromoImg}
            />
          )}

          {hasPlanCards && (
            <PlanCards lang={lang} market={market} plans={plans} sharedData={plansSharedData} />
          )}

          <NetworkSection
            lang={lang}
            market={market}
            networkHeadline={networkHeadline}
            networkSet={networkSet}
            customNetworks={customNetworks}
            ctaHrefOverride={isAnchor}
            ctaText={ctaText}
            disableFreeTrial={disableFreeTrial}
          />
          <ChannelSection lang={lang} market={market} />
          <DealSection lang={lang} market={market} />

          <PlatformSection
            lang={lang}
            market={market}
            ctaHrefOverride={isAnchor}
            ctaText={ctaText}
            disableFreeTrial={disableFreeTrial}
          />
          <ExtraCTABanner
            ctaText={ctaText}
            ctaHrefOverride={isAnchor}
            market={market}
            lang={lang}
            disableFreeTrial={disableFreeTrial}
          />
          <FAQSection lang={lang} market={market} team={team} additionalFAQs={additionalFAQs} />
        </PageWrapper>
      </LogoSyncContextProvider>
    </Layout>
  )
}

export default LatinoTemplateDefault

const PageWrapper = styled.div`
  .hero-content-text {
    h1 span {
      display: inline-block;
    }
  }
`
